import React, { useEffect, useState } from 'react';
import { api } from './auth';

/**
 * AccountAPIKeys — customer-facing API key management.
 *
 * Lets company admins manage their own company's OpenAI + Anthropic API keys
 * (BYOK mode only). Operates on /account/keys endpoints which act on the
 * current user's company — no need to know company IDs.
 *
 * Verifiers can view the page (so they can see their company's setup status)
 * but the inputs are disabled (the backend rejects PATCHs from verifiers).
 *
 * Three states:
 *   - "no keys yet"  : show 3-step guided setup
 *   - "keys set, untested" or "keys set, tested OK" : show status + rotate/clear
 *   - "keys set, test failed" : show big red error + re-test action
 */
export default function AccountAPIKeys({ user }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openaiInput, setOpenaiInput] = useState('');
  const [anthropicInput, setAnthropicInput] = useState('');
  const [vulavulaInput, setVulavulaInput] = useState('');
  const [showOpenai, setShowOpenai] = useState(false);
  const [showAnthropic, setShowAnthropic] = useState(false);
  const [showVulavula, setShowVulavula] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/account/keys');
      setStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadAudit = async () => {
    try {
      const r = await api.get('/account/keys/audit');
      setAudit(r.data);
      setShowAudit(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load audit log');
    }
  };

  const saveKeys = async () => {
    if (!openaiInput && !anthropicInput && !vulavulaInput) {
      setError('Enter at least one key to save');
      return;
    }
    setSaving(true); setError(null); setTestResult(null);
    try {
      const payload = {};
      if (openaiInput) payload.openai_api_key = openaiInput;
      if (anthropicInput) payload.anthropic_api_key = anthropicInput;
      if (vulavulaInput) payload.vulavula_api_key = vulavulaInput;
      await api.patch('/account/keys', payload);
      setOpenaiInput(''); setAnthropicInput(''); setVulavulaInput('');
      setShowOpenai(false); setShowAnthropic(false); setShowVulavula(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const testKeys = async () => {
    setTesting(true); setTestResult(null); setError(null);
    try {
      const r = await api.post('/account/keys/test');
      setTestResult(r.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  const clearKey = async (provider) => {
    const human = provider === 'all' ? 'ALL keys' : `the ${provider} key`;
    if (!window.confirm(
      `Clear ${human}?\n\nVerifications for your company will fail until new keys are entered. ` +
      `Make sure you have a replacement key ready before clearing.`
    )) return;
    try {
      await api.delete(`/account/keys?provider=${provider}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────

  if (loading) {
    return <div style={card}><p style={{ color: '#9CA3AF' }}>Loading…</p></div>;
  }

  // Error state — show the error clearly instead of staying on Loading forever
  if (error || !status) {
    const errText = typeof error === 'string' ? error : (error ? JSON.stringify(error) : '');
    const isSuperAdminWithoutCompany = errText.toLowerCase().includes('not attached to a company');
    return (
      <div style={card}>
        <h2 style={h2}>API Keys</h2>
        {isSuperAdminWithoutCompany ? (
          <div style={{ padding: 16, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, marginTop: 12 }}>
            <p style={{ fontSize: 13, margin: 0, color: '#1E40AF', lineHeight: 1.6 }}>
              <strong>Super-admin account.</strong> You're not attached to a specific company, so this page doesn't apply to you directly.
            </p>
            <p style={{ fontSize: 12, margin: '10px 0 0 0', color: '#374151', lineHeight: 1.6 }}>
              To manage a customer's API keys, go to <strong>Admin → Companies</strong>, find the company in the list,
              and click the <strong>Keys</strong> action button next to it.
            </p>
          </div>
        ) : (
          <div style={{ padding: 16, background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, marginTop: 12 }}>
            <p style={{ fontSize: 13, margin: 0, color: '#991B1B', lineHeight: 1.6 }}>
              <strong>Could not load API keys.</strong>
            </p>
            <p style={{ fontSize: 12, margin: '8px 0 0 0', color: '#374151', lineHeight: 1.5 }}>
              {error || 'Unknown error'}
            </p>
            <button onClick={load} style={{ ...btnGhost, marginTop: 12 }}>Try again</button>
          </div>
        )}
      </div>
    );
  }

  const hasAnyKey = status.has_openai_key || status.has_anthropic_key || status.has_vulavula_key;
  const hasBothKeys = status.has_openai_key && status.has_anthropic_key;
  const lastTestOK = status.keys_last_test_status === 'ok';

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={h2}>API Keys</h2>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0 0' }}>
            {status.company_name}
            {status.saas_tier && <> · <span style={{ textTransform: 'capitalize' }}>{status.saas_tier}</span> tier</>}
          </p>
        </div>
        {!status.can_modify && (
          <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, background: '#F3F4F6', color: '#6B7280', fontWeight: 600 }}>
            VIEW ONLY
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
          {error}
        </div>
      )}

      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
        <strong>About these keys:</strong> Your OpenAI, Anthropic, and optional Vulavula keys are used to process mandate
        verifications on your behalf. AI usage is billed directly to you by each provider. Eyez encrypts your keys at rest
        with industry-standard encryption (Fernet / AES-128) and never logs them. Once entered, the plaintext is never
        displayed in the UI again.
      </div>

      {/* ─── Empty state: 3-step guided setup ─── */}
      {!hasAnyKey && status.can_modify && (
        <SetupGuide
          openaiInput={openaiInput} setOpenaiInput={setOpenaiInput}
          anthropicInput={anthropicInput} setAnthropicInput={setAnthropicInput}
          vulavulaInput={vulavulaInput} setVulavulaInput={setVulavulaInput}
          showOpenai={showOpenai} setShowOpenai={setShowOpenai}
          showAnthropic={showAnthropic} setShowAnthropic={setShowAnthropic}
          showVulavula={showVulavula} setShowVulavula={setShowVulavula}
          saving={saving} onSave={saveKeys}
        />
      )}

      {/* ─── Empty state for non-admin: instructions ─── */}
      {!hasAnyKey && !status.can_modify && (
        <div style={{ padding: 16, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, fontSize: 13, color: '#92400E', lineHeight: 1.6 }}>
          <strong>API keys are not yet set up for {status.company_name}.</strong><br />
          Verifications cannot run until your company admin adds OpenAI and Anthropic API keys here.
          Please ask your company admin to log in and complete the setup.
        </div>
      )}

      {/* ─── Configured state: status + rotate ─── */}
      {hasAnyKey && (
        <>
          <Section title="Current keys">
            <div style={{ background: '#F8F9FB', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB' }}>
              <KeyRow
                label="OpenAI"
                hint="Used for Whisper voice transcription"
                masked={status.openai_key_masked}
                set={status.has_openai_key}
                canModify={status.can_modify}
                onClear={() => clearKey('openai')}
              />
              <KeyRow
                label="Anthropic"
                hint="Used for field extraction, multi-mandate detection, and PDF OCR"
                masked={status.anthropic_key_masked}
                set={status.has_anthropic_key}
                canModify={status.can_modify}
                onClear={() => clearKey('anthropic')}
              />
              <KeyRow
                label="Vulavula"
                hint="Optional. African language transcription: isiZulu, Sesotho, Afrikaans"
                masked={status.vulavula_key_masked}
                set={status.has_vulavula_key}
                canModify={status.can_modify}
                onClear={() => clearKey('vulavula')}
                optional
                last
              />
            </div>

            {status.keys_set_at && (
              <p style={{ fontSize: 11, color: '#6B7280', marginTop: 8, lineHeight: 1.5 }}>
                Last updated: {new Date(status.keys_set_at).toLocaleString()}.{' '}
                {status.keys_last_tested_at ? (
                  <>Last tested: {new Date(status.keys_last_tested_at).toLocaleString()}, {' '}
                  {lastTestOK
                    ? <span style={{ color: '#16A34A', fontWeight: 600 }}>OK</span>
                    : <span style={{ color: '#DC2626', fontWeight: 600 }}>{(status.keys_last_test_status || '').replace(/_/g, ' ')}</span>}
                  </>
                ) : (
                  <span style={{ color: '#92400E' }}>Not yet tested.</span>
                )}
              </p>
            )}
            {status.keys_last_test_detail && !lastTestOK && status.keys_last_tested_at && (
              <div style={{ marginTop: 6, padding: 10, background: '#FEE2E2', borderRadius: 6, fontSize: 11, color: '#991B1B' }}>
                <strong>Test failure detail:</strong> {status.keys_last_test_detail}
              </div>
            )}
          </Section>

          {/* Action buttons for admin */}
          {status.can_modify && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {hasBothKeys && (
                <button onClick={testKeys} disabled={testing} style={{ ...btnPrimary, opacity: testing ? 0.6 : 1 }}>
                  {testing ? 'Testing…' : (lastTestOK ? 'Test again' : 'Test connection')}
                </button>
              )}
              <button onClick={() => clearKey('all')} style={{ ...btnGhost, color: '#DC2626' }}>Clear all keys</button>
              <button onClick={loadAudit} style={{ ...btnGhost }}>View audit log</button>
            </div>
          )}

          {/* Test result card */}
          {testResult && (
            <div style={{
              marginBottom: 16, padding: 14, borderRadius: 10,
              background: testResult.overall_status === 'ok' ? '#F0FDF4' : '#FEE2E2',
              border: `1px solid ${testResult.overall_status === 'ok' ? '#BBF7D0' : '#FECACA'}`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: testResult.overall_status === 'ok' ? '#166534' : '#991B1B', marginBottom: 6 }}>
                {testResult.overall_status === 'ok' ? '✓ All configured keys are working' : '✗ Test failed'}
              </div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                <strong>OpenAI:</strong> {testResult.openai_ok ? '✓' : '✗'} {testResult.openai_msg}<br />
                <strong>Anthropic:</strong> {testResult.anthropic_ok ? '✓' : '✗'} {testResult.anthropic_msg}
                {typeof testResult.vulavula_ok !== 'undefined' && (
                  <><br /><strong>Vulavula:</strong> {testResult.vulavula_ok ? '✓' : '✗'} {testResult.vulavula_msg}</>
                )}
              </div>
            </div>
          )}

          {/* Rotation form */}
          {status.can_modify && (
            <Section title="Rotate keys">
              <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
                Replace an existing key. Leave a field blank to keep the current key for that provider.
              </p>
              <Field label="New OpenAI API key (sk-…)">
                <KeyInput value={openaiInput} onChange={setOpenaiInput} show={showOpenai} setShow={setShowOpenai} placeholder="sk-..." />
              </Field>
              <div style={{ height: 10 }} />
              <Field label="New Anthropic API key (sk-ant-…)">
                <KeyInput value={anthropicInput} onChange={setAnthropicInput} show={showAnthropic} setShow={setShowAnthropic} placeholder="sk-ant-..." />
              </Field>
              <div style={{ height: 10 }} />
              <Field label="New Vulavula API key (optional, for African languages)">
                <KeyInput value={vulavulaInput} onChange={setVulavulaInput} show={showVulavula} setShow={setShowVulavula} placeholder="Vulavula token" />
              </Field>
              <button onClick={saveKeys} disabled={saving || (!openaiInput && !anthropicInput && !vulavulaInput)}
                style={{ ...btnPrimary, marginTop: 12, opacity: (saving || (!openaiInput && !anthropicInput && !vulavulaInput)) ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save new key(s)'}
              </button>
            </Section>
          )}
        </>
      )}

      {/* Audit log overlay */}
      {showAudit && (
        <div style={{ marginTop: 16, padding: 14, background: '#F8F9FB', borderRadius: 10, border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Recent key activity</h3>
            <button onClick={() => setShowAudit(false)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>Close</button>
          </div>
          {audit.length === 0 ? (
            <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>No events yet.</p>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead><tr style={{ textAlign: 'left', color: '#6B7280' }}>
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>When</th>
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>Action</th>
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>By</th>
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>IP</th>
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>Result</th>
              </tr></thead>
              <tbody>
                {audit.map(e => (
                  <tr key={e.id} style={{ borderTop: '0.5px solid #E5E7EB' }}>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{new Date(e.created_at).toLocaleString()}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{e.action}</td>
                    <td style={{ padding: '6px 8px' }}>{e.actor_email}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{e.ip_address || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {e.test_result ? (
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
                          background: e.test_result === 'ok' ? '#DCFCE7' : '#FEE2E2',
                          color: e.test_result === 'ok' ? '#166534' : '#991B1B',
                        }}>{e.test_result}</span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function SetupGuide({ openaiInput, setOpenaiInput, anthropicInput, setAnthropicInput,
                      vulavulaInput, setVulavulaInput,
                      showOpenai, setShowOpenai, showAnthropic, setShowAnthropic,
                      showVulavula, setShowVulavula,
                      saving, onSave }) {
  return (
    <div>
      <p style={{ fontSize: 13, color: '#374151', marginBottom: 14, lineHeight: 1.6 }}>
        Welcome, let's get your API keys set up. This is a one-time setup that takes about 5 minutes.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SetupStep number={1} title="Get your OpenAI API key">
          <p style={p}>
            OpenAI processes voice mandate transcriptions for your company.{' '}
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" style={a}>
              Open OpenAI keys page →
            </a>
          </p>
          <ol style={ol}>
            <li>Sign in to your OpenAI account</li>
            <li>Click <strong>"Create new secret key"</strong></li>
            <li>Give it a name like "Eyez Production"</li>
            <li>Copy the key (starts with <code>sk-…</code>)</li>
            <li>Paste it below. Note: OpenAI shows the key only once, copy it before closing the modal</li>
          </ol>
          <Field label="OpenAI API key">
            <KeyInput value={openaiInput} onChange={setOpenaiInput} show={showOpenai} setShow={setShowOpenai} placeholder="sk-..." />
          </Field>
        </SetupStep>

        <SetupStep number={2} title="Get your Anthropic API key">
          <p style={p}>
            Anthropic processes mandate field extraction, multi-mandate detection, and OCR for your company.{' '}
            <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" style={a}>
              Open Anthropic keys page →
            </a>
          </p>
          <ol style={ol}>
            <li>Sign in to your Anthropic console</li>
            <li>Click <strong>"Create Key"</strong></li>
            <li>Name it "Eyez Production" and choose your workspace</li>
            <li>Copy the key (starts with <code>sk-ant-…</code>)</li>
            <li>Paste it below</li>
          </ol>
          <Field label="Anthropic API key">
            <KeyInput value={anthropicInput} onChange={setAnthropicInput} show={showAnthropic} setShow={setShowAnthropic} placeholder="sk-ant-..." />
          </Field>
        </SetupStep>

        <SetupStep number={3} title="Optional: Vulavula key for African languages">
          <p style={p}>
            To verify voice mandates recorded in isiZulu, Sesotho, or code-switched isiZulu, add a Vulavula key.
            Vulavula is built by Lelapa AI, a South African company specialised in African language transcription.
            Skip this step if your mandates are in English only, you can add it later.{' '}
            <a href="https://vulavula.lelapa.ai/" target="_blank" rel="noreferrer" style={a}>
              Open Vulavula →
            </a>
          </p>
          <ol style={ol}>
            <li>Sign up or sign in at vulavula.lelapa.ai</li>
            <li>Go to the <strong>API Keys</strong> section and generate a key</li>
            <li>Copy the key and paste it below</li>
            <li>Note: Vulavula keys expire every 90 days, you will need to rotate them here when they do</li>
          </ol>
          <Field label="Vulavula API key (optional)">
            <KeyInput value={vulavulaInput} onChange={setVulavulaInput} show={showVulavula} setShow={setShowVulavula} placeholder="Vulavula token" />
          </Field>
        </SetupStep>

        <SetupStep number={4} title="Save and test" last>
          <p style={p}>
            Click below to encrypt and save your keys. After saving, you'll be able to click <strong>Test connection</strong>{' '}
            to verify both providers respond. Once tests pass, your team can start running verifications.
          </p>
          <button onClick={onSave} disabled={saving || (!openaiInput && !anthropicInput && !vulavulaInput)}
            style={{ ...btnPrimary, opacity: (saving || (!openaiInput && !anthropicInput && !vulavulaInput)) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save keys'}
          </button>
        </SetupStep>
      </div>
    </div>
  );
}

function SetupStep({ number, title, children, last }) {
  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'flex-start',
      paddingBottom: last ? 0 : 14,
      borderBottom: last ? 'none' : '1px solid #F3F4F6',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: '#0F2A4A', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, flexShrink: 0,
      }}>{number}</div>
      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '4px 0 8px 0' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function KeyRow({ label, hint, masked, set, canModify, onClear, optional, last }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0',
      borderBottom: last ? 'none' : '0.5px solid #E5E7EB',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {label}{' '}
          <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}>({hint})</span>
        </div>
        <div style={{ fontSize: 12, color: set ? '#374151' : '#9CA3AF', fontFamily: 'monospace', marginTop: 2 }}>
          {set ? masked : '(not set)'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
          background: set ? '#DCFCE7' : (optional ? '#F3F4F6' : '#FEE2E2'),
          color: set ? '#166534' : (optional ? '#6B7280' : '#991B1B'),
        }}>{set ? 'SET' : (optional ? 'NOT SET' : 'MISSING')}</span>
        {set && canModify && (
          <button onClick={onClear} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', color: '#DC2626' }}>Clear</button>
        )}
      </div>
    </div>
  );
}

function KeyInput({ value, onChange, show, setShow, placeholder }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        spellCheck={false}
        style={{ ...inp, fontFamily: 'monospace' }}
      />
      <button type="button" onClick={() => setShow(!show)}
        style={{ ...btnGhost, fontSize: 11, padding: '6px 12px' }}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{title}</p>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const h2 = { fontSize: 17, fontWeight: 600, margin: 0 };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnGhost = { background: 'transparent', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 };
const inp = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const p = { fontSize: 12, color: '#374151', marginBottom: 8, lineHeight: 1.5 };
const ol = { fontSize: 12, color: '#374151', paddingLeft: 18, marginTop: 0, marginBottom: 12, lineHeight: 1.6 };
const a = { color: '#2563EB', textDecoration: 'none', fontWeight: 500 };
